import {Subsidy} from './subsidy.model';
import {ApplicationContactRole} from './application-contact-role.enum';
import {Contact} from '../core/services/user/contact.model';
import {ProjectLocation} from '../services/project-location/project-location.model';
import {TaxRecuperationOption} from './tax-recuperation-option.enum';
import {Beneficiary} from '../services/beneficiary/beneficiary.model';
import {FinanceDeminimi} from './finance-deminimi.model';
import {SubsidyPartner} from '../modules/nwb/models/subsidy-partner.model';
import {TripleAnwer} from './triple-answer.enum';
import {LocationType} from '../services/project-location/location-type.enum';
import dayjs from 'dayjs';
import {SubsidyMeasure} from '../modules/jv/models/subsidy-measure.model';
import {SubsidyWorkItem} from '../modules/jv/models/subsidy-work-item.model';
import {NatureContributionPrinciple} from '../modules/nwb/models/nature-contribution-principle.enum';
import {QuestionSelectAnswers} from './question-select-answers.model';
import {OtherSubsidy} from './other-subsidy.model';

export class CompleteSubsidy extends Subsidy {
	applicationContactRole: ApplicationContactRole;
	beneficiaries: Beneficiary[];
	financeDeminimis: FinanceDeminimi[];
	financialContact: Contact | null;
	htmlDescriptions: SubsidyDescriptions | null;
	otherSubsidies: OtherSubsidy[];
	partners: SubsidyPartner[];
	projectLocations: ProjectLocation[];
	projectLocationDescription: string;
	projectTitle: string;
	questionBooleanAnswers: QuestionBooleanAnswers | null;
	questionSelectAnswers: QuestionSelectAnswers | null;
	questionTextAnswers: QuestionTextAnswers | null;
	receivedDeminimis: TripleAnwer;
	responsibleForOrganizationContact: Contact | null;
	taxRecuperation: TaxRecuperationOption | null;
	website: string;

	// jv
	measures: SubsidyMeasure[];
	works: SubsidyWorkItem[];

	// nwb
	natureContributionPrinciples: NatureContributionPrinciple[];

	constructor(obj: any = {}) {
		super(obj);

		this.applicationContactRole = obj.applicationContactRole;
		this.beneficiaries = obj.beneficiaries?.length ? obj.beneficiaries.map((b: any) => new Beneficiary(b)) : [];
		this.financeDeminimis = obj.financeDeminimis ? obj.financeDeminimis.map((fd: any) => new FinanceDeminimi(fd)) : [];
		this.financialContact = obj.financialContact ? new Contact(obj.financialContact) : null;
		this.htmlDescriptions = obj.htmlDescriptions || null;
		this.otherSubsidies = obj.otherSubsidies
			? obj.otherSubsidies.map((os: any) => new OtherSubsidy(os)).sort((a: OtherSubsidy, b: OtherSubsidy) => (a.id || 999_999) - (b.id || 999_999))
			: [];
		this.partners = obj.partners?.length ? obj.partners.map((b: any) => new SubsidyPartner(b)) : [];
		this.projectLocations = obj.projectLocations?.length ? obj.projectLocations.map((pl: any) => new ProjectLocation(pl)) : [];
		this.projectLocationDescription = obj.projectLocationDescription;
		this.projectTitle = obj.projectTitle;
		this.questionBooleanAnswers = obj.questionBooleanAnswers || null;
		this.questionSelectAnswers = obj.questionSelectAnswers || null;
		this.questionTextAnswers = obj.questionTextAnswers || null;
		this.receivedDeminimis = obj.receivedDeminimis;
		this.responsibleForOrganizationContact = obj.responsibleForOrganizationContact ? new Contact(obj.responsibleForOrganizationContact) : null;
		this.taxRecuperation = obj.taxRecuperation;
		this.website = obj.website;

		switch (this.type) {
			case ('jv'): {
				this.measures = obj.measures || [];

				if (this.projectLocations.length === 0) {
					this.projectLocations.push(new ProjectLocation({ locationType: LocationType.ADDRESS }));
				}

				this.works = obj.works?.sort((a: any, b: any) => {
					if (a.name === 'Start werken') { return -1; }
					if (b.name === 'Start werken') { return 1; }

					if (a.name === 'Oplevering werken') { return 1; }
					if (b.name === 'Oplevering werken') { return -1; }

					return dayjs(a.targetDate).diff(dayjs(b.targetDate));
				}) || [];

				break;
			}
			case ('nwb'): {
				this.natureContributionPrinciples = obj.natureContributionPrinciples || [];

				if (this.projectLocations.length === 0) {
					this.projectLocations.push(new ProjectLocation());
				}

				break;
			}
			case ('wo1'): {
				if (this.projectLocations.length === 0) {
					this.projectLocations.push(new ProjectLocation({ locationType: LocationType.ADDRESS }));
				}

				break;
			}
		}
	}
}

interface SubsidyDescriptions {
	ACCESSIBILITY: string;
	CULTURAL_LANDSCAPE_INTEGRITY: string;
	CUSTOMER_JOURNEY: string;
	DIVERSE_VISITOR_ENGAGEMENT_MIX: string;
	EVENT_PERIOD: string;
	EXPERIENCE: string;
	FUTURE_PROOFING_AND_VIABILITY: string;
	INFORMING_AND_SENSITIZING: string;
	INNOVATIVE_CREATIVE_PROJECT: string;
	INNOVATIVE_NATURE_EXPERIENCE: string;
	INVOLVEMENT_PARTICIPANTS_PROGRAM_CREATION: string;
	JV_MAINTENANCE_PLAN: string;
	JV_OCCUPANCY_INCREASE: string;
	JV_USER_DEMAND: string;
	LOCATION_PERMISSION_STATUS: string;
	MOTIVATION_CONSCIOUS_TRAVEL: string;
	MOTIVATION_GREENING: string;
	MOTIVATION_JV: string;
	MOTIVATION_LONG_TERM: string;
	MOTIVATION_STORYLINE: string;
	MOTIVATION_SUSTAINABILITY: string;
	MUSEUM_CONTEXT_MARKET_POSITION: string;
	NEEDS_ASSESSMENT: string;
	PERMANENT_INSTALLATION: string;
	PLACE_IDENTITY_EXPERIENCE: string;
	PLAN_OF_ACTION: string;
	PROJECT: string;
	PROJECT_APPROACH: string;
	PROMOTION: string;
	ROLE_WATER_NATURE_LANDSCAPE: string;
	SITE_DISTINCTION: string;
	TARGET_AUDIENCE_AND_BARRIERS: string;
	TARGET_GROUP_COLLABORATION: string;
	TARGET_GROUP_INVOLVEMENT: string;
	TEMPORARY_INSTALLATION: string;
	VISITOR_EXPERIENCE_IMPACT: string;
	VISITOR_FINANCIAL_CONTRIBUTION: string;
	VISITOR_PARTICIPATION: string;
}

interface QuestionBooleanAnswers {
	FOSSIL_FUEL_FREE_CONFIRMATION: boolean;
	MAX_U_VALUES_CONFIRMATION: boolean;
	REGIONWIDE_PROJECT: boolean;
}

interface QuestionTextAnswers {
	APPLICANT_ENTITY_NAME: string;
}
